import type { ServiceRequest } from '@medplum/fhirtypes'
import { OrderCategory } from '@seen/core/models/orders/constants'
import type { Order } from '@seen/core/models/orders/Order'
import { Button } from '@seen/ui'
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import type { JSX } from 'react'
import { useInFlightLabSubmissionsStore } from '#src/components/order/useInFlightLabSubmissionsStore.ts'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { toast } from '#src/utils/toast.ts'

interface LabSubmissionErrorDisplayProps {
  order: Order
}

/**
 * Inline banner shown on a lab order when the async Health Gorilla submission
 * captured an error. Surfaces the structured error stored in the
 * `submissionError` extension and lets the user retry without leaving the
 * page. Retry re-flips the order to `submitting` (which clears the error)
 * and re-fires the subscription handler — same code path as the original
 * dispatch, with the same lock/idempotency guarantees.
 *
 * @param props - Component props.
 * @param props.order - The order whose submission failed.
 * @returns The error banner, or null when the order is not in error state.
 */
export function LabSubmissionErrorDisplay({
  order,
}: LabSubmissionErrorDisplayProps): JSX.Element | null {
  const { seenApi } = useSeenApi()
  const addInFlight = useInFlightLabSubmissionsStore((s) => s.add)

  const retryMutation = useMutation({
    mutationFn: async () => {
      const loadingToastId = `lab-order-submission-${order.id}`
      toast.loading('Resubmitting to lab vendor – this may take some time…', {
        id: loadingToastId,
        duration: Infinity,
      })

      await seenApi.post<ServiceRequest[]>('/orders/send-to-health-gorilla', {
        reference: order.reference,
      })

      // Register in-flight AFTER dispatch confirms server marked the order
      // submitting. Adding earlier races: watcher's first `useRead` could
      // observe the order pre-mark (`isSubmitting === false`) and fire a
      // false-terminal success toast on retry.
      if (order.id) {
        addInFlight(order.id)
      }
    },
    onError: (error) => {
      const loadingToastId = `lab-order-submission-${order.id}`
      toast.dismiss(loadingToastId)
      toast.error(`Failed to resubmit lab order: ${error.message}`)
    },
  })

  if (
    order.category !== OrderCategory.Lab ||
    !order.hasSubmissionError ||
    order.isSubmitting
  ) {
    return null
  }

  return (
    <div className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-3">
      <IconAlertTriangle className="mt-0.5 size-5 shrink-0 text-destructive" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-destructive">
            Submission to lab vendor failed
          </span>
          <span className="text-xs wrap-break-word text-muted-foreground">
            {order.submissionError ?? 'Unknown error'}
          </span>
        </div>
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={retryMutation.isPending}
            loading={retryMutation.isPending}
            onClick={() => retryMutation.mutate()}
          >
            <IconRefresh className="mr-1 size-3.5" />
            Retry submission
          </Button>
        </div>
      </div>
    </div>
  )
}
